import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Papa from 'papaparse';

import { formatDate } from 'config/i18n'
import Table from './Table'
import calculateChecksum from './calculateChecksum'
import useQuery from 'hooks/useQuery'

let clone = window.structuredClone || JSON.clone

function Migrate(props) {
  let { t } = useTranslation();
  let $files = useRef(null);
  let [tables, setTables] = useState([])
  let query = useQuery(window._l(`/consumers/migrate`).substr(1))

  let remove = key => {
    setTables(tables => tables.filter(table => table.key !== key))
  }

  let ready = key => {
    remove(key)
    setTimeout(() => query.refetch(), 2000)

  }

  let handleFiles = async e => {
    try {
      let files = Array.from(e.target.files)
      let array = await Promise.all(
        files.map(file => new Promise(async resolve => {
          let table = { name: file.name, createdAt: Date.now() }
          let key = await calculateChecksum(file)
          table.key = key || Date.now().toString()
          if (tables.some(item => item.key === table.key)) return resolve()
          // Bug real (auditoría 2026-08-16): este chequeo contra los archivos
          // YA migrados estaba deshabilitado — `query` (la lista de
          // migraciones corridas, misma forma: objetos con `.key`) está en
          // el scope de este componente, así que una vez que un CSV se
          // procesaba y desaparecía de `tables` (staging local), volver a
          // seleccionar el mismo archivo ya no se detectaba como duplicado.
          if (Array.isArray(query.data) && query.data.some(item => item.key === table.key)) return resolve()

          table.file = [file]
          let reader = new FileReader();
          reader.onload = e => {
            let csv = Papa.parse(e.target.result, { skipEmptyLines: 'greedy' })
            table.csv = csv
            resolve(table)
          };

          reader.onerror = error => {
            console.error(error)
            resolve()
          }

          reader.readAsText(file);
        }))
      )

      // Bug real (auditoría 2026-08-16): este setTables usaba `tables`, una
      // foto capturada por closure al ENTRAR a handleFiles — si mientras esta
      // lectura/hasheo (async) seguía en curso el operador clickeaba
      // "Descartar" en un archivo ya cargado (remove(), que sí actualiza de
      // forma funcional), este setState pisaba el estado con la versión
      // vieja y resucitaba el archivo recién descartado. Update funcional:
      // parte del estado más reciente, no del closure.
      setTables(prev => [...prev, ...array].filter(Boolean).sort((a, b) =>
        a.createdAt - b.createdAt
      ))

      e.target.value = ""
    } catch(e) {
      console.error(e)
    }
  }

  return (
    <div className={props.className || ''}>
      {/* Bug real (auditoría 2026-08-17, responsive): sin flex-column/breakpoint,
          a 375px cada botón quedaba apretado a ~la mitad del viewport y su
          subtexto de dos líneas se partía en líneas cortas e incómodas de leer.
          Apilados en mobile y en fila desde sm en adelante. */}
      <div className="d-flex flex-column flex-sm-row justify-content-between gap-3">
        <button
          className="btn bg-white border text-primary text-start"
          onClick={() => $files.current.click()}
        >
          <div>{t('consumers_csv_upload_link', 'Cargar')}</div>
          <div className="text-secondary"><small>{t('consumers_csv_upload_description', 'Seleccione un archivo CSV para importar pacientes')}</small></div>
        </button>
        <a
          href="https://cdn.drapp.la/consumers/ejemplo-importar-pacientes.csv"
          target="_blank"
          rel="noopener noreferrer"
          className="btn bg-white border text-primary text-start"
        >
          <div>
            {t('consumers_csv_example_link', 'Descargar')}
          </div>
          <div className="text-secondary"><small>{t('consumers_csv_example_description', 'Descargar archivo CSV de ejemplo')}</small></div>
        </a>
      </div>
      {tables.length > 0 && tables.map(table => (
        <div className="mt-4" key={table.key}>
          <Table
            remove={() => remove(table.key)}
            ready={() => ready(table.key)}
            table={table}
          />
        </div>
      ))}

      {query.data?.filter(t => t.status)?.length > 0 && (
        <div className="mt-3">
          <h6 className="mb-0 ps-1">{t('files_migrated', 'Archivos procesados')}</h6>
          <div className="d-flex flex-column gap-3">
            {query.data.filter(t => t.status).map(table =>
              <div key={`${table.key}${table.runningAt}`} className="bg-white p-3 mb-1">
                <div>
                  <a href={table.file} rel="noopener noreferrer" target="_blank">{table.name}</a>
                </div>
                <div>
                  <small className="text-secondary text-first-up">{formatDate(table.runningAt, 'eeee d MMMM HH:mm')}</small> - <small>
                    {table.uid}
                  </small>
                </div>
                {/* Bug real (auditoría 2026-08-17, responsive): Estado /
                    Duplicados / Cabecera / Registros estaban concatenados en
                    un único bloque inline separado por " — ", sin
                    flex-wrap/agrupación — a 375px el texto cortaba a mitad de
                    campo, sin ninguna separación visual entre uno y otro.
                    Ahora cada campo es un chip propio dentro de un
                    contenedor flex-wrap: envuelve como unidad completa. */}
                <div className="d-flex flex-wrap gap-2">
                  <small>
                    <span className="text-first-up">{t('status', 'Estado')}</span>: <span
                      className={table.status === 'ready' ? 'text-success' : table.status === 'running' ? 'text-warning' : 'text-secondary'}
                    >{t(`consumers_csv_status_${table.status || 'unknown'}`, table.status || 'unknown')}</span>
                  </small>
                  <small>{t(`consumers_csv_identification_duplicate`, 'Duplicados')}: <span className="text-secondary">{t(table.identificationDuplicate || 'ignore', table.identificationDuplicate || 'ignore')}</span></small>
                  <small>{t('consumers_csv_first_line_header', 'Cabecera')}: <span className="text-secondary">{!!table.firstLineHeader ? t('yes', 'Si') : t('no', 'No')}</span></small>
                  <small>{t('consumers_csv_lines', 'Registros')}: <span className="text-success" title={t('consumers_csv_records_success', 'Importados')}>{table.success || 0}</span> <span className="text-secondary">+</span> <span className="text-warning" title={t('consumers_csv_records_skip', 'Ignorados')}>{table.skip || 0}</span> <span className="text-secondary">=</span> <span className="text-secondary" title={t('consumers_csv_records_records_total', 'Total')}>{table.total || 0}</span></small>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <input
        ref={$files}
        multiple
        type="file"
        className="d-none"
        id="files"
        accept=".csv"
        onChange={handleFiles}
      />


    </div>
  );
}

export default Migrate;
